// src/components/dashboard/DailyAppointmentsChart.js
import React, { useMemo, useState } from 'react';

/**
 * Daily Appointments Trend Chart
 * Line chart showing appointments, completed, and no-shows over time
 *
 * @param {Array} data - Array of daily data with { date, appointments, completed, noShows }
 * @param {number} days - Number of days to show (14 or 30)
 */
const DailyAppointmentsChart = ({ data, days = 14 }) => {
  const [hoveredPoint, setHoveredPoint] = useState(null);

  // Get last N days of data
  const chartData = useMemo(() => {
    return data.slice(-days);
  }, [data, days]);

  // Chart dimensions
  const width = 800;
  const height = 300;
  const padding = { top: 20, right: 20, bottom: 40, left: 50 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Calculate scales
  const maxValue = useMemo(() => {
    return Math.max(...chartData.map(d => Math.max(d.appointments || d.bookings || 0, d.completed, d.noShows)));
  }, [chartData]);

  const yScale = (value) => {
    return chartHeight - (value / maxValue) * chartHeight;
  };

  const xScale = (index) => {
    return (index / (chartData.length - 1)) * chartWidth;
  };

  // Generate path data for lines
  const generatePath = (dataKey) => {
    return chartData
      .map((d, i) => {
        const x = xScale(i);
        const y = yScale(d[dataKey] || d.bookings || 0);
        return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
      })
      .join(' ');
  };

  // Generate area path for appointments
  const appointmentsAreaPath = useMemo(() => {
    const topPath = chartData
      .map((d, i) => {
        const x = xScale(i);
        const y = yScale(d.appointments || d.bookings || 0);
        return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
      })
      .join(' ');

    const bottomPath = `L ${chartWidth} ${chartHeight} L 0 ${chartHeight} Z`;
    return topPath + bottomPath;
  }, [chartData]);

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  // Y-axis ticks
  const yTicks = useMemo(() => {
    const tickCount = 5;
    return Array.from({ length: tickCount }, (_, i) => {
      const value = Math.round((maxValue / (tickCount - 1)) * i);
      return { value, y: yScale(value) };
    }).reverse();
  }, [maxValue]);

  return (
    <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary border border-app-border-light dark:border-app-border rounded-2xl p-4 lg:p-6">
      <div className="mb-4">
        <h3 className="text-base lg:text-lg font-semibold text-app-text-light dark:text-app-text">
          Daily Appointments Trend
        </h3>
        <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mt-1">
          Last {days} days
        </p>
      </div>

      {/* Legend */}
      <div className="flex gap-4 mb-4 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-500 dark:bg-blue-400"></div>
          <span className="text-app-text-light dark:text-app-text">Appointments</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500 dark:bg-green-400"></div>
          <span className="text-app-text-light dark:text-app-text">Completed</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-orange-500 dark:bg-orange-400"></div>
          <span className="text-app-text-light dark:text-app-text">No Shows</span>
        </div>
      </div>

      {/* Chart */}
      <div className="overflow-x-auto">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full"
          style={{ minWidth: '600px' }}
        >
          <g transform={`translate(${padding.left}, ${padding.top})`}>
            {/* Grid lines */}
            {yTicks.map((tick, i) => (
              <g key={i}>
                <line
                  x1={0}
                  y1={tick.y}
                  x2={chartWidth}
                  y2={tick.y}
                  stroke="#374151"
                  strokeWidth="1"
                  strokeDasharray="4 4"
                  opacity="0.3"
                />
                <text
                  x={-10}
                  y={tick.y}
                  textAnchor="end"
                  alignmentBaseline="middle"
                  fill="#9CA3AF"
                  fontSize="11"
                >
                  {tick.value}
                </text>
              </g>
            ))}

            {/* Area fill for appointments */}
            <path
              d={appointmentsAreaPath}
              fill="url(#appointmentsGradient)"
              opacity="0.2"
            />

            {/* Lines */}
            <path
              d={generatePath('appointments') || generatePath('bookings')}
              fill="none"
              stroke="#60A5FA"
              strokeWidth="2.5"
            />
            <path
              d={generatePath('completed')}
              fill="none"
              stroke="#34D399"
              strokeWidth="2.5"
            />
            <path
              d={generatePath('noShows')}
              fill="none"
              stroke="#FB923C"
              strokeWidth="2.5"
            />

            {/* Data points */}
            {chartData.map((d, i) => {
              const x = xScale(i);
              const appointmentCount = d.appointments || d.bookings || 0;
              return (
                <g key={i}>
                  <circle
                    cx={x}
                    cy={yScale(appointmentCount)}
                    r="4"
                    fill="#60A5FA"
                    onMouseEnter={() => setHoveredPoint({ index: i, data: d })}
                    onMouseLeave={() => setHoveredPoint(null)}
                    className="cursor-pointer transition-all hover:r-6"
                  />
                  <circle
                    cx={x}
                    cy={yScale(d.completed)}
                    r="4"
                    fill="#34D399"
                    onMouseEnter={() => setHoveredPoint({ index: i, data: d })}
                    onMouseLeave={() => setHoveredPoint(null)}
                    className="cursor-pointer"
                  />
                  <circle
                    cx={x}
                    cy={yScale(d.noShows)}
                    r="4"
                    fill="#FB923C"
                    onMouseEnter={() => setHoveredPoint({ index: i, data: d })}
                    onMouseLeave={() => setHoveredPoint(null)}
                    className="cursor-pointer"
                  />
                </g>
              );
            })}

            {/* X-axis labels */}
            {chartData.map((d, i) => {
              // Show every nth label based on data length
              const showEvery = chartData.length > 20 ? 4 : chartData.length > 14 ? 2 : 1;
              if (i % showEvery !== 0 && i !== chartData.length - 1) return null;

              const x = xScale(i);
              return (
                <text
                  key={i}
                  x={x}
                  y={chartHeight + 20}
                  textAnchor="middle"
                  fill="#9CA3AF"
                  fontSize="10"
                >
                  {formatDate(d.date)}
                </text>
              );
            })}

            {/* Tooltip */}
            {hoveredPoint && (
              <g transform={`translate(${xScale(hoveredPoint.index)}, ${yScale(hoveredPoint.data.appointments || hoveredPoint.data.bookings || 0) - 80})`}>
                <rect
                  x="-60"
                  y="0"
                  width="120"
                  height="70"
                  fill="#1F2937"
                  stroke="#374151"
                  strokeWidth="1"
                  rx="8"
                />
                <text x="0" y="15" textAnchor="middle" fill="#E5E7EB" fontSize="10" fontWeight="600">
                  {formatDate(hoveredPoint.data.date)}
                </text>
                <text x="0" y="30" textAnchor="middle" fill="#60A5FA" fontSize="10">
                  Appointments: {hoveredPoint.data.appointments || hoveredPoint.data.bookings || 0}
                </text>
                <text x="0" y="45" textAnchor="middle" fill="#34D399" fontSize="10">
                  Completed: {hoveredPoint.data.completed}
                </text>
                <text x="0" y="60" textAnchor="middle" fill="#FB923C" fontSize="10">
                  No Shows: {hoveredPoint.data.noShows}
                </text>
              </g>
            )}
          </g>

          {/* Gradient definition */}
          <defs>
            <linearGradient id="appointmentsGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#60A5FA" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#60A5FA" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Hover instructions */}
      <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary text-center mt-2">
        Hover over data points for details
      </div>
    </div>
  );
};

export default DailyAppointmentsChart;
